import { NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PublicSchoolDto } from '@motoschool/shared';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { Avatar, cn } from './ui';
import { SchoolName } from './SchoolName';
import { Logo } from './Logo';
import { usePageMeta } from '../lib/seo';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Показывать счётчик заявок, ожидающих решения. */
  badge?: boolean;
  /**
   * Пункт только для бокового меню. На телефоне пять вкладок — предел
   * читаемости, поэтому редкие разделы туда не выносим: до них есть
   * ссылка из профиля.
   */
  desktopOnly?: boolean;
  /** Раздел доступен только администраторам. */
  adminOnly?: boolean;
}

const STAFF_NAV: NavItem[] = [
  { to: '/schedule', label: 'Расписание', icon: '🗓' },
  { to: '/requests', label: 'Заявки', icon: '📨', badge: true },
  { to: '/students', label: 'Ученики', icon: '👥' },
  { to: '/overview', label: 'Сводка', icon: '📊' },
  { to: '/working-hours', label: 'Часы работы', icon: '⏰', desktopOnly: true },
  { to: '/staff', label: 'Сотрудники', icon: '🧑‍🏫', desktopOnly: true, adminOnly: true },
  { to: '/settings', label: 'Школа', icon: '🏫', desktopOnly: true, adminOnly: true },
  { to: '/profile', label: 'Профиль', icon: '⚙️' },
];

const STUDENT_NAV: NavItem[] = [
  { to: '/schedule', label: 'Расписание', icon: '🗓' },
  { to: '/book', label: 'Записаться', icon: '➕' },
  { to: '/my-requests', label: 'Заявки', icon: '📨' },
  { to: '/balance', label: 'Баланс', icon: '💳' },
  { to: '/profile', label: 'Профиль', icon: '⚙️' },
];

export function Layout(): React.JSX.Element {
  const { user, hasRole } = useAuth();

  /*
   * Кабинет закрыт от индексации целиком: в выдаче ему делать нечего, а
   * адреса вроде /students/<id> ведут на данные учеников. Один Layout — на
   * все его страницы, поэтому и метка одна.
   */
  usePageMeta({
    title: 'Личный кабинет — FREEDOM BIKER',
    description: 'Расписание занятий, заявки и баланс часов.',
    path: '/schedule',
    noindex: true,
  });
  const isStaff = hasRole('TEACHER', 'ADMIN');
  const isAdmin = hasRole('ADMIN');

  const navItems = (isStaff ? STAFF_NAV : STUDENT_NAV).filter(
    (item) => !item.adminOnly || isAdmin,
  );
  const mobileNavItems = navItems.filter((item) => !item.desktopOnly);

  /*
   * Название школы приходит из настроек, а не вписано в код: администратор
   * меняет его сам, и в шапке кабинета оно должно совпадать с тем, что
   * видит гость на публичной странице.
   */
  const { data: school } = useQuery({
    queryKey: ['public', 'school'],
    queryFn: () => api.get<PublicSchoolDto>('/public/school', undefined, { anonymous: true }),
    staleTime: 5 * 60_000,
  });
  const schoolName = school?.name ?? 'Мотошкола';

  const { data: pending } = useQuery({
    queryKey: ['requests', 'pending-count'],
    queryFn: () => api.get<{ count: number }>('/requests/pending-count'),
    enabled: isStaff,
    refetchInterval: 60_000,
  });

  const pendingCount = pending?.count ?? 0;

  return (
    <div className="min-h-dvh lg:flex">
      {/* Боковое меню появляется только на широких экранах */}
      <aside className="hidden w-60 shrink-0 border-r border-line bg-surface lg:block">
        <div className="flex items-center gap-3 px-5 py-6">
          <Logo className="size-10" />
          <div className="min-w-0">
            <SchoolName name={schoolName} className="block text-base" />
            <p className="text-xs text-muted">Расписание и учёт</p>
          </div>
        </div>
        <nav className="space-y-1 px-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive ? 'bg-brand-50 text-brand-700' : 'text-ink hover:bg-slate-50',
                )
              }
            >
              <span aria-hidden>{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.badge && pendingCount > 0 && (
                <span className="rounded-full bg-danger px-1.5 py-0.5 text-xs font-semibold text-white">
                  {pendingCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="safe-top sticky top-0 z-30 border-b border-line bg-surface/90 px-4 pb-3 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <Logo className="size-9" />
              <div className="min-w-0">
                <SchoolName name={schoolName} className="block truncate text-sm leading-tight" />
                <p className="truncate text-xs text-muted">{user?.name}</p>
              </div>
            </div>
            {user && <Avatar name={user.name} size="sm" />}
          </div>
        </header>

        {/* pb-24 оставляет место под нижнюю навигацию на телефоне */}
        <main className="mx-auto w-full max-w-4xl flex-1 px-4 pt-4 pb-24 lg:px-8 lg:pt-8 lg:pb-8">
          <Outlet />
        </main>

        <nav className="safe-bottom fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-surface/95 px-1 pt-1 backdrop-blur lg:hidden">
          {mobileNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'relative flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg text-[11px] font-medium',
                  isActive ? 'text-brand-600' : 'text-muted',
                )
              }
            >
              <span className="text-lg" aria-hidden>
                {item.icon}
              </span>
              {item.label}
              {item.badge && pendingCount > 0 && (
                <span className="absolute right-1/4 top-1 rounded-full bg-danger px-1.5 text-[10px] font-semibold text-white">
                  {pendingCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
